import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'lodash-es';
import { useRef } from 'react';
import { GroupedVirtuoso, GroupedVirtuosoHandle } from 'react-virtuoso';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { BulkActionsDrawer } from 'src/tiles/BulkActionsContent';
import { MobileEventTilesBulkActions } from 'src/tiles/EventTile';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  EventWithData,
  SaleMetrics,
} from 'src/WebApiController';

import { useCollapsibleGroups } from '../SaleEventsMobile.hooks';
import {
  EventTilesGroupHeader,
  EventTilesGroupItem,
} from './EventGroupedList.components';
import * as styles from './EventGroupedList.css';
import { useSaleBulkActions } from './EventGroupedList.hooks';

export const EventGroupedTiles = ({
  eventsTransformed,
}: {
  eventsTransformed?: EventWithData[];
}) => {
  const { eventMetrics } = useCatalogMetricsContext<SaleMetrics>();
  const { data } = useCatalogDataContext();

  const { selectionMode, itemSelection, setSelectionMode } =
    useMultiSelectionContext();
  const [showBulkActions, { toggle: toggleBulkActions }] = useDisclosure();

  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const scrollRef = useRef<HTMLElement | null>(null);

  const {
    events,
    eventGroups,
    updateGroupCollapsed,
    getEventFromVirtruosoGroup,
  } = useCollapsibleGroups(eventsTransformed ?? [], eventMetrics, data);

  const saleActionGroups = useSaleBulkActions(false);

  if (events.length === 0) {
    return (
      <EmptySectionContent.Root
        icon={
          <EmptySectionContent.SolidIconContainer>
            <SearchSolidIcon />
          </EmptySectionContent.SolidIconContainer>
        }
      >
        <EmptySectionContent.Label>
          <Content id={ContentId.NoResultFound} />
        </EmptySectionContent.Label>
        <EmptySectionContent.DetailMessage>
          <Content id={ContentId.NoEventsMatching} />
        </EmptySectionContent.DetailMessage>
      </EmptySectionContent.Root>
    );
  }

  return (
    <>
      <GroupedVirtuoso
        id={'virtuoso-event-purchases-mobile'}
        scrollerRef={(el) => {
          if (el instanceof HTMLElement) scrollRef.current = el;
        }}
        className={styles.virtuosoRoot}
        overscan={window.innerHeight}
        increaseViewportBy={window.innerHeight}
        groupCounts={eventGroups.map((group) =>
          group.collapsed ? 0 : group.count
        )}
        groupContent={(index) => {
          const { name, count, collapsed } = eventGroups[index];
          return (
            <EventTilesGroupHeader
              key={name}
              index={index}
              groupName={name}
              groupCount={count}
              virtuosoHandler={virtuosoRef.current}
              collapsed={collapsed}
              updateCollapse={updateGroupCollapsed(index)}
            />
          );
        }}
        itemContent={(index, groupIndex) => {
          const event = getEventFromVirtruosoGroup(index, groupIndex);
          if (event == null) {
            return null;
          }
          return (
            <EventTilesGroupItem
              event={event.event}
              saleCount={event.counts.salesCnt ?? 0}
              metrics={eventMetrics?.[event.event.virtId]}
              selectionMode={selectionMode}
            />
          );
        }}
      />
      <MobileEventTilesBulkActions
        entityType={ActionOutboxEntityType.Sale}
        events={events ?? []}
        onBulkActionsClick={toggleBulkActions}
      />
      <BulkActionsDrawer
        entityType={ActionOutboxEntityType.Sale}
        actionGroups={saleActionGroups}
        onClose={() => {
          setSelectionMode(undefined);
          toggleBulkActions();
        }}
        show={!isEmpty(itemSelection) && showBulkActions}
      />
    </>
  );
};
